// src/pages/ResearchPage.tsx
import React, { useState } from 'react';
import styles from '../styles/ResearchPage.module.css';

interface Publication {
  id: number;
  title: string;
  date: string;
  type: string;
  badge?: string;
  excerpt: string;
  author: string;
  imageUrl: string;
}

interface Grant {
  id: number;
  title: string;
  organization: string;
  period: string;
  status: 'active' | 'completed';
  amount: string;
  description: string;
}

interface Innovation {
  id: number;
  title: string;
  author: string;
  date: string;
  excerpt: string;
  icon: string;
}

interface Partner {
  id: number;
  name: string;
  logoUrl: string;
  description: string;
  stats: { label: string; value: string }[];
  link: string;
}

const dummyPublications: Publication[] = [
  {
    id: 1,
    title: 'Understanding Consciousness in AI',
    date: '2025-06-15',
    type: 'Journal Article',
    badge: 'NEW',
    excerpt: 'An in-depth analysis of machine consciousness...',
    author: 'Jane Doe',
    imageUrl: 'https://source.unsplash.com/random/400x300?brain',
  },
  {
    id: 2,
    title: 'Ethics of Emerging Technologies',
    date: '2025-05-20',
    type: 'Conference Paper',
    excerpt: 'Exploring moral frameworks for novel tech...',
    author: 'John Smith',
    imageUrl: 'https://source.unsplash.com/random/400x300?technology',
  },
  // …more items
];

const dummyGrants: Grant[] = [
  {
    id: 1,
    title: 'AI & Society Fellowship',
    organization: 'Global Thinkers Fund',
    period: '2025–2026',
    status: 'active',
    amount: '$50,000',
    description: 'Supports research on AI integration in society.',
  },
  {
    id: 2,
    title: 'Philosophy Research Grant',
    organization: 'Humanities Trust',
    period: '2024–2025',
    status: 'completed',
    amount: '$30,000',
    description: 'Funded studies in classical ethics.',
  },
  // …more items
];

const dummyInnovations: Innovation[] = [
  {
    id: 1,
    title: 'Quantum Mind Hypothesis',
    author: 'Alice Brown',
    date: '2025-04-10',
    excerpt: 'Proposes a quantum basis for consciousness...',
    icon: '🧠',
  },
  {
    id: 2,
    title: 'Blockchain for Knowledge Sharing',
    author: 'Bob Green',
    date: '2025-03-22',
    excerpt: 'Using distributed ledgers to share academic resources...',
    icon: '🔗',
  },
  // …more items
];

const dummyPartners: Partner[] = [
  {
    id: 1,
    name: 'Global Research Institute',
    logoUrl: 'https://source.unsplash.com/random/120x120?institution',
    description: 'A leading center for interdisciplinary research.',
    stats: [
      { label: 'Projects', value: '120+' },
      { label: 'Publications', value: '300+' },
    ],
    link: '#',
  },
  {
    id: 2,
    name: 'Tech Ethics Lab',
    logoUrl: 'https://source.unsplash.com/random/120x120?lab',
    description: 'Focuses on ethical implications of new tech.',
    stats: [
      { label: 'Studies', value: '45' },
      { label: 'Grants', value: '10' },
    ],
    link: '#',
  },
  // …more items
];

const ResearchPage: React.FC = () => {
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  // Filter & search logic for publications
  const filteredPubs = dummyPublications.filter(pub =>
    (filter === 'All' || pub.type === filter) &&
    pub.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPubs = filteredPubs.length;
  const totalPages = Math.ceil(totalPubs / perPage);
  const pagedPubs = filteredPubs.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className={styles['research-page-container']}>
      <main className={styles['research-main-content']}>

        {/* Header */}
        <header className={styles['research-header']}>
          <div className={styles['research-header-overlay']}>
            <h1>Cutting-Edge Philosophy Research</h1>
            <p>Discover publications, grants, innovations, and partnerships shaping the future of thought.</p>
          </div>
          {/* <div className={styles['research-header-decoration']}>
            <div className={styles['decoration-circle']}>📖</div>
            <div className={styles['decoration-circle']}>🧠</div>
            <div className={styles['decoration-circle']}>🌐</div>
          </div> */}
        </header>

        {/* Publications Section */}
        <section className={styles['research-section']}>
          <div className={styles['section-header']}>
            <h2 className={styles['section-title']}>Latest Publications</h2>
            <div className={styles['section-filter']}>
              <select value={filter} onChange={e => { setFilter(e.target.value); setCurrentPage(1); }}>
                <option>All</option>
                <option>Journal Article</option>
                <option>Conference Paper</option>
              </select>
              <div className={styles['search-box']}>
                <input
                  type="text"
                  placeholder="Search titles…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <button>🔍</button>
              </div>
            </div>
          </div>

          <div className={styles['publications-grid']}>
            {pagedPubs.map(pub => (
              <div key={pub.id} className={styles['publication-card']}>
                <div className={styles['pub-image-container']}>
                  <img src={pub.imageUrl} alt={pub.title} />
                  <span className={styles['pub-type']}>{pub.type}</span>
                </div>
                <div className={styles['pub-content']}>
                  <div className={styles['pub-meta']}>
                    <span className={styles['pub-date']}>{pub.date}</span>
                    {pub.badge && <span className={styles['pub-badge']}>{pub.badge}</span>}
                  </div>
                  <h3>{pub.title}</h3>
                  <p>{pub.excerpt}</p>
                  <div className={styles['pub-author']}><strong>By:</strong> {pub.author}</div>
                  <div className={styles['pub-actions']}>
                    <a href="#" className={styles['read-more']}>Read More</a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className={styles['pagination-container']}>
            <div className={styles['pagination-info']}>
              Showing {pagedPubs.length} of {totalPubs} publications
            </div>
            <div className={styles['pagination-controls']}>
              <button
                className={`${styles['pagination-button']} prev`}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`${styles['pagination-button']} ${page === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className={`${styles['pagination-button']} next`}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
            </div>
          </div>
        </section>

        {/* Grants & Fellowships */}
        <section className={styles['research-section']}>
          <h2 className={styles['section-title']}>Grants & Fellowships</h2>
          <div className={styles['grants-list-container']}>
            {dummyGrants.map(grant => (
              <div key={grant.id} className={styles['grant-item']}>
                <span className={`${styles['grant-status']} ${styles[grant.status]}`}>
                  {grant.status === 'active' ? 'Open' : 'Closed'}
                </span>
                <div className={styles['grant-details']}>
                  <h3>{grant.title}</h3>
                  <div className={styles['grant-meta']}>
                    <span className={styles['grant-org']}>{grant.organization}</span>
                    <span className={styles['grant-period']}>{grant.period}</span>
                  </div>
                  <p className={styles['grant-desc']}>{grant.description}</p>
                </div>
                <div className={styles['grant-amount']}>{grant.amount}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Innovations & Theories */}
        <section className={styles['research-section']}>
          <h2 className={styles['section-title']}>Innovations & Theories</h2>
          <div className={styles['innovations-grid-container']}>
            {dummyInnovations.map(item => (
              <div key={item.id} className={styles['innovation-card']}>
                <div className={styles['innovation-icon']}><div className={styles['icon-circle']}>{item.icon}</div></div>
                <div className={styles['innovation-content']}>
                  <h3>{item.title}</h3>
                  <p>{item.excerpt}</p>
                  <div className={styles['innovation-meta']}>
                    <span className={styles['innovation-author']}>{item.author}</span>
                    <span>{item.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Collaborations & Partnerships */}
        <section className={styles['research-section']}>
          <h2 className={styles['section-title']}>Collaborations & Partnerships</h2>
          <div className={styles['partnerships-grid']}>
            {dummyPartners.map(partner => (
              <div key={partner.id} className={styles['partner-card']}>
                <div className={styles['partner-logo-container']}>
                  <img src={partner.logoUrl} alt={partner.name} className={styles['partner-logo']} />
                </div>
                <div className={styles['partner-info']}>
                  <h3>{partner.name}</h3>
                  <p className={styles['partner-desc']}>{partner.description}</p>
                  <div className={styles['partner-stats']}>
                    {partner.stats.map((s, i) => (
                      <div key={i} className={styles['stat-item']}>
                        <span className={styles['stat-value']}>{s.value}</span>
                        <span className={styles['stat-label']}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                  <a href={partner.link} className={styles['partner-link']}>Learn More</a>
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>

     
    </div>
  );
};

export default ResearchPage;
